import React, { ComponentProps } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { ReactComponent as ChevronRightIcon } from '@/assets/icons/chevron_right.svg';
import { ReactComponent as ClockIcon } from '@/assets/icons/clock.svg';

import styles from './component.module.scss';
import { TableRow } from './TableRow/component.tsx';
import { TableCell } from './TableCell/component.tsx';
import { Pagination } from './Pagination/component.tsx';

type Datum = Record<string, React.ReactNode>;

const Table = ({
  title,
  viewMoreLinkTo,
  columns,
  data,
  className,
  pagination,
  pleaseCheckBackMessage
}: {
  title: string;
  viewMoreLinkTo?: string;
  columns: {
    key: string;
    title: React.ReactNode;
    mobile?: false;
    style?: ComponentProps<typeof TableCell>['style'];
    alignment?: ComponentProps<typeof TableCell>['alignment'];
  }[];
  data: Datum[];
  pleaseCheckBackMessage?: boolean;
  className?: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (pageNumber: number) => void;
  };
}) => {
  return (
    <div className={clsx(styles.main, className)}>
      <h5>{title}</h5>
      <div className={styles.tableWrapper}>
        {data.length > 0 && (
          <table>
            <thead>
              <TableRow>
                {columns.map((column, cidx) => (
                  <TableCell header key={cidx}>
                    {column.title}
                  </TableCell>
                ))}
              </TableRow>
            </thead>
            <tbody>
              {data.map((row, ridx) => (
                <TableRow key={ridx}>
                  {columns.map((column, cidx) => (
                    <TableCell
                      key={`${ridx}-${cidx}`}
                      style={column.style}
                      alignment={column.alignment}
                    >
                      {row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </tbody>
          </table>
        )}
        {data.length === 0 && (
          <div className={styles.emptyState}>
            <ClockIcon />
            <span>No data available</span>
            {pleaseCheckBackMessage && <span>Please check back soon</span>}
          </div>
        )}
      </div>
      {viewMoreLinkTo && data.length > 0 && (
        <div className={styles.viewMore}>
          <Link to={viewMoreLinkTo}>
            View More <ChevronRightIcon />
          </Link>
        </div>
      )}
      {pagination && (
        <Pagination {...pagination} className={styles.pagination} />
      )}
    </div>
  );
};

export { Table };
